import React, { Fragment, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { closeModal } from "../../store/modal/modalReducer";
import { getBooking } from "../../store/booking/bookingActions";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";

export default function ModalSearch(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchInput = useRef(null);
  const [searchCollections, setSearchCollections] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(0);
  const { current_staff } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.settings);
  const unitInput = useRef(null);
  const licensePlateInput = useRef(null);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [selectedTag, setSelectedTag] = useState(0);
  const [selectedAosStatus, setSelectedAosStatus] = useState("");
  const [favoriteBookings, setFavoriteBookings] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(true);

  // Fetch properties and favorites on component mount
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}properties`
        );
        // Ensure we have valid properties data
        if (response.data && response.data.length > 0) {
          setProperties(response.data);
        } else {
          console.warn("No properties data received");
          setProperties([]);
          setSelectedProperty(0);
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    const fetchFavorites = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}favorite-bookings`
        );
        setFavoriteBookings(response.data || []);
      } catch (error) {
        console.error("Error fetching favorites:", error);
      } finally {
        setLoadingFavorites(false);
      }
    };

    fetchProperties();
    fetchFavorites();
  }, []);
  useEffect(() => {
    const fetchFoldersAndTags = async () => {
      try {
        const [foldersRes, tagsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}folders`),
          axios.get(`${process.env.REACT_APP_API_URL}tags`)
        ]);
        
        if (foldersRes.data?.folders) {
          setFolders(foldersRes.data.folders);
        }
        if (tagsRes.data?.tags) {
          setTags(tagsRes.data.tags);
        }
      } catch (error) {
        console.error("Error fetching folders/tags:", error);
      }
    };
  
    fetchFoldersAndTags();
  }, []);
  // Handle property selection change
  const handlePropertyChange = (e) => {
    const selectedValue = e.target.value ? parseInt(e.target.value, 10) : 0;
    setSelectedProperty(selectedValue);
  };
  const isSalesPlatform = () => {
      // For QP, check specific environments - set to true for Charlie, Ladybacks, Dear Jean
      if (process.env.REACT_APP_COLOUR === "QP") {
        const apiUrl = process.env.REACT_APP_API_URL;
        return (
          apiUrl === "https://charlie-api.romiapp.co.za" ||
          apiUrl === "https://ladybacks-api.romiapp.co.za" ||
          apiUrl === "https://dearjean-api.romiapp.co.za" 
          // uncomment for testing sales platforms on local
          || apiUrl === "http://romi-backend.test/api/"
        );
      }
    return false;
  };
  async function searchFrm(e) {
    if (e.code === "Enter" || e === false) {
      setSearching(true);
      await axios
        .post(`${process.env.REACT_APP_API_URL}searchBookings`, {
          search: searchInput.current.value,
          unitSearch: unitInput.current.value,
          licensePlate: licensePlateInput.current?.value || "",
          propertyId: selectedProperty,
          folderLocation: selectedFolder,
          tagId: selectedTag,
          aosStatus: selectedAosStatus,
        })
        .then((response) => {
          let allresults = response.data;
          let newResults = [];

          // First filter based on staff restrictions
          allresults.forEach(function (r) {
            if (
              current_staff.restrict_views === 0 ||
              (current_staff.restrict_views === 1 &&
                (r.tag === 4 || r.tag === 21))
            ) {
              newResults.push(r);
            }
          });

          // Filter based on collections if needed
          if (searchCollections === true) {
            newResults = newResults.filter((r) => r.location === "Residents");
          }

          setSearchResults(newResults);
          setSearching(false);
        })
        .catch((error) => {
          setSearchResults([]);
          setSearching(false);
        });
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <ErrorBoundary>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-2 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:max-w-7xl sm:p-6">
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <Dialog.Title
                    as="h1"
                    className="text-lg font-medium text-gray-900"
                  >
                    <div>
                      {/* Favorites Section */}
                      {!loadingFavorites && favoriteBookings.length > 0 && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                              <span className="text-yellow-500 mr-2">⭐</span>
                              Favorites ({favoriteBookings.length}/10)
                            </h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {favoriteBookings.map((fav) => (
                              <Link
                                key={fav.id}
                                to={`/booking/${fav.location?.toLowerCase().replace(/\s/g, "")}?user_id=${fav.user_id}&booking_id=${fav.id}`}
                                onClick={() => {
                                  dispatch(getBooking(fav.id));
                                  dispatch(closeModal());
                                }}
                                className="block p-2 bg-white border border-gray-200 rounded hover:border-primary hover:shadow-sm transition-all"
                              >
                                <div className="text-xs font-semibold text-gray-900">
                                  {fav.booking_reference}
                                </div>
                                <div className="text-xs text-gray-600 truncate">
                                  {fav.name} {fav.surname}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {fav.cell}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {fav.location}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row justify-between gap-2 sm:items-center">
                        <div className="grow">
                          <label className="block text-sm font-medium mb-2 text-gray-700">
                            Search
                          </label>
                        </div>
                        {current_staff.restrict_views === 0 && (
                          <div className="flex gap-4 items-center">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                onChange={(e) =>
                                  setSearchCollections(e.target.checked)
                                }
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                Collections
                              </span>
                            </div>
                            {/* Renewals checkbox commented out as in original code */}
                          </div>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-4">
                        {/* Left Column */}
                        <input
                          className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                          ref={searchInput}
                          placeholder="Type something to search"
                          onKeyUp={(e) => searchFrm(e)}
                        />
                        {process.env.REACT_APP_COLOUR !== "UC" && !isSalesPlatform() && (
                          <input
                            type="text"
                            className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                            ref={licensePlateInput}
                            placeholder="License Plate"
                            onKeyUp={(e) => searchFrm(e)}
                          />
                        )}
                        <select
                          value={selectedProperty || ""}
                          onChange={handlePropertyChange}
                          className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                        >
                          <option value="">All Properties</option>
                          {properties.map((property) => (
                            <option key={property.value} value={property.value}>
                              {property.label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={selectedFolder}
                          onChange={(e) => {
                            setSelectedFolder(e.target.value);
                            setSelectedTag(0); // Reset tag when folder changes
                          }}
                          className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                        >
                          <option value="">All Locations</option>
                          {folders.map((folder) => (
                            <option key={folder.id} value={folder.name}>
                              {folder.name}
                            </option>
                          ))}
                        </select>

                        {/* Conditional tag dropdown - only show if selected folder has_tags */}
                        {selectedFolder && folders.find(f => f.name === selectedFolder)?.has_tags === 1 && (
                          <select
                            value={selectedTag}
                            onChange={(e) => setSelectedTag(parseInt(e.target.value, 10))}
                            className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                          >
                            <option value={0}>All Tags</option>
                            {tags
                              .filter(tag => {
                                const folder = folders.find(f => f.name === selectedFolder);
                                return folder && tag.parent_folder === folder.id;
                              })
                              .map((tag) => (
                                <option key={tag.id} value={tag.id}>
                                  {tag.name}
                                </option>
                              ))}
                          </select>
                        )}
                        <input
                          type="text"
                          className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                          ref={unitInput}
                          placeholder="Unit"
                          onKeyUp={(e) => searchFrm(e)}
                        />
                        {process.env.REACT_APP_COLOUR === "QP" && (
                          <select
                            value={selectedAosStatus}
                            onChange={(e) => setSelectedAosStatus(e.target.value)}
                            className="block w-full lg:flex-1 lg:min-w-[140px] font-light border border-gray-300 shadow-sm py-2 px-2 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                          >
                            <option value="">All AOS Status</option>
                            <option value="pending">Pending</option>
                            <option value="pending_payment">Pending Payment</option>
                            <option value="reserved">Reserved</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="signed">Signed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="waitlisted">Waitlisted</option>
                          </select>
                        )}
                        <button
                          className="w-full sm:w-auto lg:flex-none text-white uppercase px-4 py-2 rounded-lg border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 h-[38px]"
                          onClick={() => searchFrm(false)}
                        >
                          Search
                        </button>
                      </div>
                    </div>

                    <div className="mt-5">
                      {searching ? (
                        <div className="text-center">Searching...</div>
                      ) : (
                        <>
                          {searchResults &&
                          searchResults.length > 0 &&
                          searchResults !== "blank" ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                      Booking
                                    </th>
                                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                      Name
                                    </th>
                                    <th className="hidden sm:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                      Location
                                    </th>

                                    {(!process.env.REACT_APP_HIDE_CHAT_TABS ||
                                      process.env.REACT_APP_HIDE_CHAT_TABS ===
                                        "false") && (
                                      <>
                                        <th className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                          Bed
                                        </th>
                                        <th className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                          MDA TC
                                        </th>
                                        <th className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                          Student No
                                        </th>
                                      </>
                                    )}

                                    {process.env.REACT_APP_HIDE_CHAT_TABS ===
                                      "true" && (
                                      <th className="hidden sm:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Unit/s
                                      </th>
                                    )}

                                    <th className="hidden sm:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                      Status
                                    </th>
                                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {searchResults.map((res) => (
                                    <tr
                                      key={`search_${res.id}`}
                                      className={
                                        res.booking_merged_into !== null
                                          ? "bg-red-100"
                                          : ""
                                      }
                                    >
                                      <td className="whitespace-normal py-4 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-6">
                                        {res.booking_merged_into !== null && (
                                          <div className="font-bold">
                                            <p>
                                              Merged Into:
                                              <br />#{res.user_merged_into}-
                                              {res.booking_merged_into}
                                            </p>
                                            <hr className="border-red-600" />
                                          </div>
                                        )}
                                        {res.booking_reference} ({res.year}){" "}
                                        {res.active_booking === res.id
                                          ? "(Active)"
                                          : ""}
                                      </td>
                                      <td className="whitespace-normal px-3 py-4 text-xs text-gray-500">
                                        <p>
                                          {res.name} {res.surname}
                                        </p>
                                        <p className="hidden sm:block">
                                          {res.email}
                                        </p>
                                        <p className="hidden sm:block">
                                          {res.cell}
                                        </p>
                                        <p className="hidden sm:block">
                                          {res.id_number}
                                        </p>
                                      </td>
                                      <td className="hidden sm:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                        {res.location}
                                      </td>

                                      {(!process.env.REACT_APP_HIDE_CHAT_TABS ||
                                        process.env.REACT_APP_HIDE_CHAT_TABS ===
                                          "false") && (
                                        <>
                                          <td className="hidden md:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                            {res.bed}
                                          </td>
                                          <td className="hidden md:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                            {res.mda_tenant_id}
                                          </td>
                                          <td className="hidden md:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                            {res.student_number}
                                          </td>
                                        </>
                                      )}

                                      {process.env.REACT_APP_HIDE_CHAT_TABS ===
                                        "true" && (
                                        <td className="hidden sm:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                          {res.aos_units}
                                        </td>
                                      )}

                                      <td className="hidden sm:table-cell whitespace-normal px-3 py-4 text-xs text-gray-500">
                                        {res.archived === 1 && <p>Archived</p>}
                                        {res.closed === 1 && <p>Closed</p>}
                                        {res.declined === 1 && <p>Declined</p>}
                                        {!res.archived &&
                                          !res.closed &&
                                          !res.declined && <p>Open</p>}
                                      </td>
                                      <td className="py-4">
                                        <Link
                                          to={
                                            searchCollections === false
                                              ? res.broker_info === 1
                                                ? `/booking/brokers?user_id=${res.user_id}&booking_id=${res.id}`
                                                : res.booking_merged_into !==
                                                  null
                                                ? res.declined
                                                  ? `/booking/declined?user_id=${res.user_merged_into}&booking_id=${res.booking_merged_into}`
                                                  : res.tag
                                                  ? `/booking/management?tag=${res.tag}&user_id=${res.user_merged_into}&booking_id=${res.booking_merged_into}`
                                                  : `/booking/${
                                                      res.archived == 1
                                                        ? "archived"
                                                        : res.location
                                                            ?.toLowerCase()
                                                            .replace(/\s/g, "")
                                                    }?user_id=${
                                                      res.user_merged_into
                                                    }&booking_id=${
                                                      res.booking_merged_into
                                                    }`
                                                : res.declined
                                                ? `/booking/declined?user_id=${res.user_id}&booking_id=${res.id}`
                                                : res.tag
                                                ? `/booking/management?tag=${res.tag}&user_id=${res.user_id}&booking_id=${res.id}`
                                                : `/booking/${
                                                    res.archived == 1
                                                      ? "archived"
                                                      : res.location
                                                          ?.toLowerCase()
                                                          .replace(/\s/g, "")
                                                  }?user_id=${
                                                    res.user_id
                                                  }&booking_id=${res.id}`
                                              : `/booking/collections?collection_tag=-1&user_id=${res.user_id}&booking_id=${res.id}`
                                          }
                                          onClick={() => {
                                            dispatch(
                                              getBooking(
                                                res.booking_merged_into == null
                                                  ? res.id
                                                  : res.booking_merged_into
                                              )
                                            );
                                            dispatch(closeModal());
                                          }}
                                          className="block w-full sm:w-auto text-center text-white uppercase px-4 py-2 rounded-lg border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                        >
                                          Go
                                        </Link>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center">No results found!</div>
                          )}
                        </>
                      )}
                    </div>
                  </Dialog.Title>
                </div>
              </div>
            </Transition.Child>
          </div>
        </ErrorBoundary>
      </Dialog>
    </Transition.Root>
  );
}
